import type { CSSProperties, FC, ReactNode } from 'react'
import type { ImagePlateProps } from '@/types'

import { useEffect, useState } from 'react'

import { cn } from '@/lib/utils'
import { useReveal } from '@/hooks/use-reveal'
import { useParallax } from '@/hooks/use-parallax'

import { EASE, MICRO_FAINT } from '@/constants/type'

/**
 * A photographic plate. A curtain in the page colour slides up off the image
 * while the image itself settles out of a slight over-scale — the same weight
 * and easing as the wall.
 *
 * The drift and the reveal live on two different nodes so the parallax
 * transform never fights the reveal.
 */
export const ImagePlate: FC<ImagePlateProps> = ({
    src,
    alt,
    ratio = '3 / 4',
    focus = '50% 38%',
    delay = 0,
    drift = 0,
    trigger = 'scroll',
    wipe = 'up',
    priority = false,
    caption,
    className
}): ReactNode => {
    const { ref: revealRef, shown } = useReveal<HTMLDivElement>(0.08)
    const driftRef = useParallax<HTMLDivElement>(drift)
    const [mounted, setMounted] = useState(false)

    useEffect(() => {
        setMounted(true)
    }, [])

    const open = trigger === 'mount' ? mounted : shown

    return (
        <figure ref={driftRef} className={cn('m-0', className)}>
            <div
                ref={revealRef}
                className='relative overflow-hidden bg-fz-wash'
                style={{ aspectRatio: ratio }}
            >
                <img
                    src={src}
                    alt={alt}
                    loading={priority ? 'eager' : 'lazy'}
                    draggable={false}
                    className='block h-full w-full object-cover'
                    style={
                        {
                            objectPosition: focus,
                            transform: open ? 'scale(1)' : 'scale(1.14)',
                            transition: `transform 1.5s ${EASE} ${delay}s`
                        } as CSSProperties
                    }
                />

                <span
                    aria-hidden='true'
                    className='absolute inset-0 bg-fz-bg'
                    style={{
                        transform: open
                            ? wipe === 'side'
                                ? 'translateX(101%)'
                                : 'translateY(-101%)'
                            : 'translate(0, 0)',
                        transition: `transform ${wipe === 'side' ? '1.35s' : '1.15s'} ${EASE} ${delay}s`
                    }}
                />

                <span
                    aria-hidden='true'
                    className='pointer-events-none absolute inset-0'
                    style={{ boxShadow: 'inset 0 0 0 1px rgba(255,255,255,0.08)' }}
                />
            </div>

            {caption ? (
                <figcaption
                    className={`mt-3 transition-opacity duration-700 ${MICRO_FAINT}`}
                    style={{ opacity: open ? 1 : 0, transitionDelay: `${delay + 0.5}s` }}
                >
                    {caption}
                </figcaption>
            ) : null}
        </figure>
    )
}
